// React imports
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, unstable_usePrompt } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import queryString from 'query-string';

// Mui imports
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import { green, red } from '@mui/material/colors';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck, faRotateRight } from '@fortawesome/pro-light-svg-icons';

// Local imports
import { minimizeProducts, maximizeProducts, getErrors } from '../utils/opportunity.js';
import { markOpportunityDirty, markOpportunityClean } from '../utils/dirtyOpportunities.js';
import OpportunityHeader from './OpportunityHeader.js';
import OpportunityOneOffAccountRouteProductsSelector from './OpportunityOneOffAccountRouteProductsSelector.js';
import { reconcileTemplateDefaultCosts } from './OpportunityOneOffBuilder.addtemplate.js';
import { unlinkMismatchedRates } from '../utils/rates.js';
import PRODUCT_TYPES from '../../ProductTypes.js';
import { getDefaultEmailPrefaceForEmailPage } from './OpportunityOneOffEmailBuilder.utils.js';
import email from '../utils/email.js';
import { FinancialSummaryBar } from './OpportunityPreviewAndSendBase.js';

function getDefaultPreface(org, link) {
    return getDefaultEmailPrefaceForEmailPage({ organization: org, link });
}

function OpportunityOneOffScreen(props) {
    const { type, Builder, me, accounts, seaPorts, unlocode, suppliers,
        opportunity, onUpdateOpportunity, exchangeRates, onDuplicate,
        accesstoken, org, minimizedSearch, forceStep, onCreateTemplate,
        possibleTags, contracts, PreviewAndSend, featureToggle, setAccounts,
        emailLoginInfo, onEmailRefreshConnection, onEmailLogout, opportunities
    } = props;

    const { emailConversationId, attachments: attachmentsEncoded } = queryString.parse(window.location.search);

    let [ dirty, setDirty ] = useState(opportunity === null);
    let [ status, setStatus ] = useState(opportunity === null ? '' : opportunity.status);
    const [ routes, setRoutes ] = useState(opportunity.routes || []);
    const [ incoterm, setIncoterm ] = useState(opportunity.incoterm || null);
    const [ lockedExchangeRates, setLockedExchangeRates ] = useState(opportunity === null ? {} : opportunity.exchangeRates || {});
    const [ errors, setErrors ] = useState({});
    const [ tags, setTags ] = useState(opportunity === null ? [] : opportunity.tags);
    const [ activeStep, setActiveStep ] = useState(-1);
    const [ onNextLoading, setOnNextLoading ] = useState(false);
    const [ onNextDisabled, setOnNextDisabled ] = useState(false);
    const [ saveSuccess, setSaveSuccess ] = useState(false);
    const [ saving, setSaving ] = useState(false);
    const [ saveError, setSaveError ] = useState(null);
    const [ saveDisabled, setSaveDisabled ] = useState(false);
    const [ showReopen, setShowReopen ] = useState(null);
    const [ ownership, setOwnership ] = useState(null);
    const [ tagsError, setTagsError ] = useState(false);
    const [ preface, setPreface ] = useState(opportunity === null ? null : (opportunity.type === 'one-off-free-text' && !opportunity.preface && opportunity.status === 'draft') ? getDefaultPreface(org, 'https://PLACEHOLDER_FOR_LINK') : opportunity.preface || null);
    const [ epilogue, setEpilogue ] = useState(opportunity === null ? null : opportunity.epilogue || null);
    const [ source ] = useState(opportunity === null ?
        (emailConversationId ? { type: 'email', id: emailConversationId?.split(':')[0], globalMessageId: emailConversationId?.split(':')[1] || '' } : null) : opportunity.source || null);
    const [ backgroundEmailMessage, setBackgroundEmailMessage ] = useState(null);

    const opportunityId = (opportunity || {}).id || null;
    const transportationMethod = opportunity.transportationMethod || null;
    const products = useMemo(() => maximizeProducts({ products: opportunity.products || [] }), [opportunity.products]);

    const account = useMemo(() => {
        if (!opportunity?.account?.id) return {};
        return accounts.find(a => a.id === opportunity?.account?.id);
    }, [opportunity, accounts]);

    window.searchPaneLoadFromOpportunity = {
        opportunityId: opportunityId || 'new',
        transportationMethod,
        routes,
        products,
    };

    const dirtyRef = useRef(dirty);
    useEffect(() => {
        dirtyRef.current = dirty;
    }, [dirty]);
    useEffect(() => {
        window.onbeforeunload = () => {
            if (dirtyRef.current) {
                return 'You have unsaved changes. Are you sure you want to leave?';
            }
        }
        return () => {
            window.onbeforeunload = null;
        };
    }, [])

    unstable_usePrompt({
        message: 'You have unsaved changes. Are you sure you want to leave?',
        when: ({ currentLocation, nextLocation }) => {
            return dirtyRef.current;
        }
    });

    // Sync status when opportunity prop changes (e.g., after Mark as Won from header)
    useEffect(() => {
        if (opportunity.status && opportunity.status !== status) setStatus(opportunity.status);
    }, [opportunity.status]);

    // Resume polling if opportunity is in 'sending-to-endcustomer' state (e.g., after page refresh)
    useEffect(() => {
        if (opportunity.status === 'sending-to-endcustomer' && opportunity.pendingEmailId) {
            // Get recipient info from the log
            const log = opportunity.log?.findLast(l => l.action === 'sending-to-endcustomer');
            let toRecipients = [];
            let ccRecipients = [];
            if (log && log.reason) {
                try {
                    const reason = JSON.parse(log.reason);
                    toRecipients = reason.toRecipients || [];
                    ccRecipients = reason.ccRecipients || [];
                } catch (e) {
                    console.error('Failed to parse log reason', e);
                }
            }

            // Poll for email completion
            (async () => {
                try {
                    const pollResult = await email.waitForEmailSent({
                        org,
                        emailId: opportunity.pendingEmailId,
                        monadaAccesstoken: accesstoken
                    });

                    if (pollResult.success) {
                        // Auto-lock exchange rates on send if not already locked
                        if (!lockedExchangeRates || _.isEmpty(lockedExchangeRates)) {
                            const locked = { organization: org.exchangeRates, live: exchangeRates, locked: new Date().getTime() };
                            setLockedExchangeRates(locked);
                        }
                        // Email sent successfully, update opportunity status
                        const { data: updatedOpportunity } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${org.id}&accesstoken=${accesstoken}`, {
                            opportunity: {
                                status: 'sent-to-endcustomer',
                                account: opportunity.account,
                                pendingEmailId: null,
                                exchangeRates: lockedExchangeRates || { organization: org.exchangeRates, live: exchangeRates, locked: new Date().getTime() },
                            },
                            reason: JSON.stringify({
                                source: 'email-sent',
                                toRecipients,
                                ccRecipients
                            })
                        });
                        onUpdateOpportunity({ opportunity: { ...opportunity, ...updatedOpportunity } });
                        setStatus(updatedOpportunity.status);
                        setBackgroundEmailMessage({ severity: 'success', message: `Quote sent successfully to ${toRecipients[0]?.address || 'recipient'}` });
                    } else {
                        setBackgroundEmailMessage({ severity: 'error', message: `Failed to send email: ${pollResult.error || 'Unknown error'}` });
                    }
                } catch (e) {
                    setBackgroundEmailMessage({ severity: 'error', message: `Error sending quote: ${e.toString()}` });
                }
            })();
        }
    }, [opportunity.id, opportunity.status, opportunity.pendingEmailId]);

    // Listen for email-sent events from the sidebar notification
    useEffect(() => {
        const handleEmailSent = async (event) => {
            const { opportunityId: emailOpportunityId, to } = event.detail;

            // Only handle if this email was for the current opportunity
            if (emailOpportunityId && emailOpportunityId === opportunity.id) {
                console.log('[OpportunityOneOffBaseScreen] Email sent event received for this opportunity:', event.detail);

                // Fetch the updated opportunity from the server
                // (the worker already updated the status)
                try {
                    const { data: updatedOpportunity } = await axios.get(`/api/opportunities/${opportunity.id}?organizationId=${org.id}&accesstoken=${accesstoken}`);
                    onUpdateOpportunity({ opportunity: { ...opportunity, ...updatedOpportunity } });
                    setStatus(updatedOpportunity.status);

                    if (updatedOpportunity.status === 'sent-to-endcustomer') {
                        // Auto-lock exchange rates on send if not already locked
                        if (!lockedExchangeRates || _.isEmpty(lockedExchangeRates)) {
                            const locked = { organization: org.exchangeRates, live: exchangeRates, locked: new Date().getTime() };
                            setLockedExchangeRates(locked);
                            // Persist the locked rates
                            await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${org.id}&accesstoken=${accesstoken}`, {
                                opportunity: { exchangeRates: locked }
                            });
                        }
                        const recipientAddress = to?.[0]?.address || 'recipient';
                        setBackgroundEmailMessage({ severity: 'success', message: `Quote sent successfully to ${recipientAddress}` });
                    }
                } catch (e) {
                    console.error('[OpportunityOneOffBaseScreen] Failed to refresh opportunity after email sent:', e);
                }
            }
        };

        window.addEventListener('email-sent', handleEmailSent);
        return () => window.removeEventListener('email-sent', handleEmailSent);
    }, [opportunity.id, org.id, accesstoken, onUpdateOpportunity]);

    useEffect(() => {
        setActiveStep(ac => {
            if (ac > -1) return ac;
            if (forceStep) return parseInt(forceStep);
            if (opportunity.status === 'draft' && type === 'one-off-email') return 1;
            if (!opportunity.status || opportunity.status === 'draft') return 1;
            if (opportunity.status === 'deleted') return 0;
            return 2;
        });
    }, [opportunityId, forceStep])

    // Initialize viewTemplate from org defaults when opportunity is first created
    useEffect(() => {
        if (opportunity && opportunityId && (opportunity.status === 'draft' || !opportunity.status) && !opportunity.attributes?.viewTemplate) {
            const defaultViewTemplate = org?.defaults?.viewTemplate || 'standard';
            if (defaultViewTemplate !== 'standard') {
                onUpdateOpportunity({ 
                    opportunityId, 
                    func: opportunity => ({ 
                        ...opportunity, 
                        attributes: { 
                            ...(opportunity.attributes || {}), 
                            viewTemplate: defaultViewTemplate 
                        } 
                    }) 
                });
            }
        }
    }, [opportunity?.id, opportunity?.status, opportunity?.attributes?.viewTemplate, org?.defaults?.viewTemplate, opportunityId])

    function setBuyingRates(a) {
        setDirty(true);
        if (_.isFunction(a)) {
            onUpdateOpportunity({ opportunityId, func: opportunity => ({ ...opportunity, buyingRates: a(opportunity.buyingRates).map(br => ({ ...br, product: minimizeProducts({ products: [br.product] })[0] })) }) });
        } else {
            onUpdateOpportunity({ opportunityId, func: opportunity => ({ ...opportunity, buyingRates: a.map(br => ({ ...br, product: minimizeProducts({ products: [br.product] })[0] })) }) });
        }
    }

    async function setUpdates({ updates }) {
        setDirty(true);

        if (opportunity.status === 'draft') {
            onUpdateOpportunity({ opportunityId, func: opportunity => ({ ...opportunity, updates }) });
            return;
        }

        try {
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { opportunity: { updates } });
            onUpdateOpportunity({ opportunity: _opportunity });
        } catch(e) {
        }
    }

    function setProducts(a) {
        setDirty(true);
        // After products change (add / type-change / mode swap all route through here):
        // 1. rates priced for a container type a product no longer has are unlinked from it. They
        //    stay in the Cost Center as not used, to add again where they fit.
        // 2. the template's default costs are re-applied for any product that is new or changed
        //    type and isn't yet linked. Products that were already there keep the costs the user gave them.
        const withReconcile = (opportunity, products) => {
            const unlinked = unlinkMismatchedRates({ offers: opportunity.offers, products, buyingRates: opportunity.buyingRates });
            const updated = { ...opportunity, products, offers: unlinked.offers };
            const rec = reconcileTemplateDefaultCosts({ opportunity: updated, forms: org.forms, previousProducts: opportunity.products || [] });
            return rec.changed ? { ...updated, offers: rec.offers, buyingRates: rec.buyingRates } : updated;
        };
        const newProducts = _.isFunction(a) ? a(opportunity?.products || []) : a;
        tellUnlinkedRates(unlinkMismatchedRates({ offers: opportunity?.offers, products: newProducts, buyingRates: opportunity?.buyingRates }).removed);
        if (_.isFunction(a)) {
            onUpdateOpportunity({ opportunityId, func: opportunity => withReconcile(opportunity, a(opportunity.products)) });
        } else {
            onUpdateOpportunity({ opportunityId, func: opportunity => withReconcile(opportunity, a) });
        }
    }

    // Says which rates a product change unlinked. Template default costs are left out: the template
    // puts the right ones back for the new container type.
    function tellUnlinkedRates(removed) {
        const rates = _.uniqBy(removed.filter(({ rate }) => rate.platform !== -2), ({ rate }) => rate.id);
        if (!rates.length) return;
        const types = _.uniq(rates.map(({ rate }) => rate.product.type));
        const many = rates.length > 1;
        setBackgroundEmailMessage({
            severity: 'info',
            message: `${rates.length} rate${many ? 's were' : ' was'} priced for ${types.join(', ')} and ${many ? 'were' : 'was'} removed from the changed product. ${many ? 'They are' : 'It is'} still in the Cost Center, to add again.`,
        });
    }

    function _setTransportationMethod(a) {
        const newProducts = _.compact(products.flatMap(product => {
            if (a === transportationMethod) return product;
            if (PRODUCT_TYPES[a].find(pp => pp.name === product.type)) return product;
            // LCL → Air: LCL calculations become Air freight calculations
            if (product.type === 'LCL' && a === 'air') {
                return {
                    // Keep the same product id across this 1:1 conversion so template default
                    // costs / sell rates (keyed by product id) survive an Air↔Sea round-trip
                    // and reappear when the mode returns to the original.
                    id: product.id,
                    type: 'Air freight',
                    kgOrLbs: product.kgOrLbs,
                    cmOrInches: product.cmOrInches,
                    nonstackable: product.nonstackable,
                    dangerous: product.dangerous,
                    dangerousClass: product.dangerousClass,
                    dangerousUNCode: product.dangerousUNCode,
                    dangerousMSDS: product.dangerousMSDS,
                    roundChargeableWeight: true,
                    calculations: (product.calculations || []).map(calc => ({
                        ...calc,
                        id: calc.id || uuidv4(),
                        // dimensions→dimensions, volume→volume (direct mapping)
                    })),
                };
            }
            // LCL → Land: expand each calculation into a land product
            if (product.type === 'LCL' && a === 'land') {
                return (product.calculations || []).map(calc => ({
                    id: uuidv4(),
                    type: 'By Dimensions or Weight (land)',
                    kgOrLbs: product.kgOrLbs,
                    cmOrInches: product.cmOrInches,
                    quantity: calc.quantity || 1,
                    weight: calc.weight,
                    volume: calc.method === 'volume' ? calc.volume : undefined,
                    commodity: calc.commodity,
                    dangerous: product.dangerous,
                    dangerousClass: product.dangerousClass,
                    dangerousUNCode: product.dangerousUNCode,
                    dangerousMSDS: product.dangerousMSDS,
                }));
            }
            // Air freight (unified) → Sea: becomes LCL with calculations
            if (product.type === 'Air freight' && a === 'sea') {
                return {
                    // Keep the same product id across this 1:1 conversion so template default
                    // costs / sell rates (keyed by product id) survive an Air↔Sea round-trip
                    // and reappear when the mode returns to the original.
                    id: product.id,
                    type: 'LCL',
                    kgOrLbs: product.kgOrLbs,
                    cmOrInches: product.cmOrInches,
                    nonstackable: product.nonstackable,
                    dangerous: product.dangerous,
                    dangerousClass: product.dangerousClass,
                    dangerousUNCode: product.dangerousUNCode,
                    dangerousMSDS: product.dangerousMSDS,
                    densityRatio: 1000,
                    calculations: (product.calculations || []).map(calc => ({
                        id: calc.id || uuidv4(),
                        method: calc.method === 'chargeable-weight' ? 'volume' : calc.method,
                        quantity: calc.quantity,
                        weight: calc.weight,
                        commodity: calc.commodity,
                        ...(calc.method === 'dimensions' ? { length: calc.length, width: calc.width, height: calc.height } : {}),
                        ...(calc.method === 'volume' ? { volume: calc.volume } : {}),
                        ...(calc.method === 'chargeable-weight' ? { volume: 0 } : {}),
                    })),
                };
            }
            // Air freight (unified) → Land: expand calculations into land products
            if (product.type === 'Air freight' && a === 'land') {
                return (product.calculations || []).map(calc => ({
                    id: uuidv4(),
                    type: 'By Dimensions or Weight (land)',
                    kgOrLbs: product.kgOrLbs,
                    cmOrInches: product.cmOrInches,
                    quantity: calc.quantity || 1,
                    weight: calc.weight,
                    commodity: calc.commodity,
                    dangerous: product.dangerous,
                    dangerousClass: product.dangerousClass,
                    dangerousUNCode: product.dangerousUNCode,
                    dangerousMSDS: product.dangerousMSDS,
                    ...(calc.method === 'dimensions' ? { length: calc.length, width: calc.width, height: calc.height } : {}),
                    ...(calc.method === 'volume' ? { volume: calc.volume } : {}),
                }));
            }
            if (product.type === 'By Dimensions or Weight (land)' && a === 'sea') return ({ ...product, type: 'By Dimensions or Weight (sea)'});
            // Land → Air: land W/M products become Air freight calculations
            if (product.type === 'By Dimensions or Weight (land)' && a === 'air') {
                return null; // handled below in landToAir aggregation
            }
            if (product.type === 'By Dimensions or Weight (sea)' && a === 'land') return ({ ...product, type: 'By Dimensions or Weight (land)'});
            // Sea W/M → Air: handled below in seaWmToAir aggregation
            if (product.type === 'By Dimensions or Weight (sea)' && a === 'air') {
                return null; // handled below in seaWmToAir aggregation
            }
            if (product.type === 'Air Freight' && a === 'land') return ({ ...product, type: 'By Dimensions or Weight (land)'});
            if (product.type === 'Air Freight' && a === 'sea') return ({ ...product, type: 'By Dimensions or Weight (sea)'});
            if (product.type === 'By Volume or Weight (sea)' && a === 'air') {
                return null; // handled below in seaWmToAir aggregation
            }
            return null
        }));

        // Land → Air: aggregate land W/M products into a single Air freight product
        if (a === 'air') {
            const landWmProducts = products.filter(p => p.type === 'By Dimensions or Weight (land)');
            if (landWmProducts.length > 0) {
                const firstLand = landWmProducts[0];
                newProducts.push({
                    id: uuidv4(),
                    type: 'Air freight',
                    kgOrLbs: firstLand.kgOrLbs || 'kg',
                    cmOrInches: firstLand.cmOrInches || 'cm',
                    nonstackable: firstLand.nonstackable || false,
                    dangerous: firstLand.dangerous || false,
                    dangerousClass: firstLand.dangerousClass,
                    dangerousUNCode: firstLand.dangerousUNCode,
                    dangerousMSDS: firstLand.dangerousMSDS,
                    roundChargeableWeight: true,
                    calculations: landWmProducts.map(p => ({
                        id: uuidv4(),
                        method: (p.length && p.width && p.height) ? 'dimensions' : (p.volume ? 'volume' : 'dimensions'),
                        quantity: p.quantity || 1,
                        weight: p.weight,
                        commodity: p.commodity,
                        ...(p.length && p.width && p.height ? { length: p.length, width: p.width, height: p.height } : {}),
                        ...(p.volume && !(p.length && p.width && p.height) ? { volume: p.volume } : {}),
                    })),
                });
            }
            // Sea W/M → Air: aggregate sea W/M products into Air freight calculations
            const seaWmProducts = products.filter(p => p.type === 'By Dimensions or Weight (sea)' || p.type === 'By Volume or Weight (sea)');
            if (seaWmProducts.length > 0) {
                const firstSea = seaWmProducts[0];
                // Check if an Air freight product was already created (from land or LCL)
                const existingAf = newProducts.find(p => p.type === 'Air freight');
                const newCalcs = seaWmProducts.map(p => ({
                    id: uuidv4(),
                    method: (p.length && p.width && p.height) ? 'dimensions' : (p.volume ? 'volume' : 'dimensions'),
                    quantity: p.quantity || 1,
                    weight: p.weight,
                    commodity: p.commodity,
                    ...(p.length && p.width && p.height ? { length: p.length, width: p.width, height: p.height } : {}),
                    ...(p.volume && !(p.length && p.width && p.height) ? { volume: p.volume } : {}),
                }));
                if (existingAf) {
                    existingAf.calculations = [...(existingAf.calculations || []), ...newCalcs];
                } else {
                    newProducts.push({
                        id: uuidv4(),
                        type: 'Air freight',
                        kgOrLbs: firstSea.kgOrLbs || 'kg',
                        cmOrInches: firstSea.cmOrInches || 'cm',
                        nonstackable: firstSea.nonstackable || false,
                        dangerous: firstSea.dangerous || false,
                        dangerousClass: firstSea.dangerousClass,
                        dangerousUNCode: firstSea.dangerousUNCode,
                        dangerousMSDS: firstSea.dangerousMSDS,
                        roundChargeableWeight: true,
                        calculations: newCalcs,
                    });
                }
            }
            // Old Air Freight → Air freight: aggregate old Air Freight products into Air freight calculations
            const oldAirProducts = products.filter(p => p.type === 'Air Freight');
            if (oldAirProducts.length > 0) {
                const firstOld = oldAirProducts[0];
                const existingAf = newProducts.find(p => p.type === 'Air freight');
                const newCalcs = oldAirProducts.map(p => ({
                    id: uuidv4(),
                    method: (p.length && p.width && p.height) ? 'dimensions' : (p.volume ? 'volume' : (p.chargeableWeight ? 'chargeable-weight' : 'dimensions')),
                    quantity: p.quantity || 1,
                    weight: p.weight,
                    commodity: p.commodity,
                    ...(p.length && p.width && p.height ? { length: p.length, width: p.width, height: p.height } : {}),
                    ...(p.volume && !(p.length && p.width && p.height) ? { volume: p.volume } : {}),
                    ...(p.chargeableWeight ? { chargeableWeight: p.chargeableWeight } : {}),
                }));
                if (existingAf) {
                    existingAf.calculations = [...(existingAf.calculations || []), ...newCalcs];
                } else {
                    newProducts.push({
                        id: uuidv4(),
                        type: 'Air freight',
                        kgOrLbs: firstOld.kgOrLbs || 'kg',
                        cmOrInches: firstOld.cmOrInches || 'cm',
                        nonstackable: firstOld.nonstackable || false,
                        dangerous: firstOld.dangerous || false,
                        dangerousClass: firstOld.dangerousClass,
                        dangerousUNCode: firstOld.dangerousUNCode,
                        dangerousMSDS: firstOld.dangerousMSDS,
                        roundChargeableWeight: true,
                        calculations: newCalcs,
                    });
                }
            }
        }

        setDirty(true);
        onUpdateOpportunity({ opportunityId, func: opportunity => ({ ...opportunity, transportationMethod: a }) });
        if (!_.isEqual(products, newProducts)) {
            setProducts(newProducts);
        }
    }

    function setAccount(a) { 
        if (!a.id) {
            a.id = uuidv4();
            a.status = 'draft';
            setAccounts(acc => [...acc, a]);
        }
        setDirty(true); 
        onUpdateOpportunity({ opportunityId, func: opportunity => ({ ...opportunity, account: a }) });
    }
    function _setIncoterm(a) { setDirty(true); setIncoterm(a); }
    function _setPreface(a) { 
        if (a !== preface) { 
            setDirty(true); 
            setPreface(a); 
            onUpdateOpportunity({ opportunityId, func: opportunity => ({ ...opportunity, preface: a }) });
        }
    }

    const description = opportunity?.description || {};
    function _setDescription(a) { 
        setDirty(true); 
        onUpdateOpportunity({ opportunityId, func: opportunity => ({ ...opportunity, description: a }) });
    }

    const attachPDF = opportunity?.attributes?.attachPDF || false;
    function _setAttachPDF(a) {
        setDirty(true);
        onUpdateOpportunity({ opportunityId, func: opportunity => ({ ...opportunity, attributes: { ...(opportunity.attributes || {}), attachPDF: a } }) });
    }

    function _setEpilogue(a) { if (a !== epilogue) { setDirty(true); setEpilogue(a); } }
    function _setOffers(a) { 
        setDirty(true); 
        onUpdateOpportunity({ opportunityId, func: opportunity => ({ ...opportunity, offers: _.isFunction(a) ? a(opportunity.offers) : a }) });
        setErrors({ ...errors, builder: [] });
    }
    function _setRoutes(a) { 
        setDirty(true); 
        setRoutes(a); 
        onUpdateOpportunity({ opportunityId, func: opportunity => ({ ...opportunity, routes: a }) });
    }
    async function _setTags({ tags }) {
        setDirty(true);
        setTagsError(false);
        setTags(tags);
        if (opportunity.status === 'draft') return;
        try {
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { opportunity: { tags } });
            onUpdateOpportunity({ opportunity: _opportunity });
        } catch(e) {
            setTagsError(true);
        }
    }

    async function onDelete() {
        setStatus('deleted');
        status = 'deleted';
        setDirty(true);
        dirty = true;
        setOnNextDisabled(true);
        await save();
        setOnNextDisabled(false);
    }

    async function onSave({ force = false } = {}) {
        setOnNextDisabled(true);
        const res = await save({ force });
        setOnNextDisabled(false);
        if (res) {
            setSaveSuccess(true);
            await new Promise(res => setTimeout(res, 2000));
            setSaveSuccess(false);
        }
        return res;
    }
    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;

    async function onPrevStep() {
        if (activeStep === 2) {
            setActiveStep(1);
        }
    }

    async function onNextStep() {
        if (status === 'deleted') {
            setActiveStep(1);
            return;
        }

        setOnNextLoading(true);
        setSaveDisabled(true);

        const success = await (async () => {
            if (dirty) {
                return await save();
             } else {
                setErrors({ header: [], account: [], products: [], incoterm: [], builder: [] });
                const er = getErrors({ 
                    opportunity: {
                        ..._.omit(opportunity, ['id', 'created', 'createdBy', 'owner', 'organizationId', 'log', 'modified']),
                        status: status === 'draft' ? null : status || null,
                        type,
                        account: _.pick(account, ['id']),
                        tags,
                        incoterm,
                        products: minimizeProducts({ products }),
                        transportationMethod,
                        routes,
                        preface,
                        epilogue,
                        source,
                        buyingRates: (opportunity.buyingRates || []).map(br => ({ ...br, product: minimizeProducts({ products: [br.product] })[0] })),
                        exchangeRates: lockedExchangeRates,
                    }
                });
                if (!_.isEmpty(er.header) || !_.isEmpty(er.account) || !_.isEmpty(er.products) || !_.isEmpty(er.incoterm) || !_.isEmpty(er.builder)) {
                    setErrors(er);
                    return false;
                }
                return true;
             }
        })();

        setOnNextLoading(false);
        setSaveDisabled(false);

        if (success && activeStep === 0) {
            setActiveStep(1);
        }
        if (success && activeStep === 1) {
            setActiveStep(2);
        }
    }

    function onSetLockedExchangeRates(lock) {
        setDirty(true);
        if (lock) {
            setLockedExchangeRates({ 
                organization: org.exchangeRates,
                live: exchangeRates,
                locked: new Date().getTime(),
            });
        } else {
            setLockedExchangeRates(null);
        }
    }

    async function save({ force = false } = {}) {
        setSaveError(null);
        setErrors({ header: [], account: [], products: [], incoterm: [], builder: []});

        const er = getErrors({
            opportunity: {
                ..._.omit(opportunity, ['id', 'created', 'createdBy', 'owner', 'organizationId', 'log', 'modified']),
                status: status === 'draft' ? null : status || null,
                type,
                account: _.pick(account, ['id']),
                tags,
                incoterm,
                products: minimizeProducts({ products }),
                transportationMethod,
                routes,
                preface,
                epilogue,
                source,
                buyingRates: (opportunity.buyingRates || []).map(br => ({ ...br, product: minimizeProducts({ products: [br.product] })[0] })),
                exchangeRates: lockedExchangeRates,
            }
        });

        if (!_.isEmpty(er.header) || !_.isEmpty(er.account) || !_.isEmpty(er.products) || !_.isEmpty(er.incoterm) || !_.isEmpty(er.builder)) {
            console.error(er);
            setErrors(er);
            return false;
        }

        let newOffers = _.cloneDeep(opportunity.offers);

        newOffers.forEach((offer, oi) => {
            if (offer.displayCurrency?.convert) {
                offer.displayCurrencySource = offer.displayCurrency.source;
                offer.displayCurrency = offer.displayCurrency.to;
            } else if (offer.displayCurrency?.convert === false) {
                delete offer.displayCurrency;
                delete offer.displayCurrencySource;
            }
            offer.sections.forEach((section, si) => {
                if (section.displayCurrency?.convert) {
                    section.displayCurrencySource = section.displayCurrency.source;
                    section.displayCurrency = section.displayCurrency.to;
                } else if (section.displayCurrency?.convert === false) {
                    delete section.displayCurrency;
                    delete section.displayCurrencySource;
                }
                section.offers.forEach((sectionOffer, soi) => sectionOffer.fields.forEach((field, fi) => {
                    if (field.displayCurrency?.convert) {
                        field.displayCurrencySource = field.displayCurrency.source;
                        field.displayCurrency = field.displayCurrency.to;
                    } else if (field.displayCurrency?.convert === false) {
                        delete field.displayCurrency;
                        delete field.displayCurrencySource;
                    }
                    if (field.type === 'per-unit') {
                        field.values = _.pick(field.values, [...products.map(p => p.id), 'custom', 'flat']);
                        const productValue = _.first(_.values(_.pick(field.values, products.map(p => p.id))));

                        if (productValue) {
                            products.forEach((product, pi) => {
                                field.values[product.id] = productValue;
                            });
                        }
                    }
                    // Clean up empty multiplierText for custom priced fields
                    if (field.type === 'custom' && field.values?.custom) {
                        if (!field.values.custom.multiplierText || field.values.custom.multiplierText.trim() === '') {
                            delete field.values.custom.multiplierText;
                        }
                        if (field.values.custom.multiplier !== undefined && field.values.custom.multiplier !== null) {
                            const parsed = parseFloat(field.values.custom.multiplier);
                            field.values.custom.multiplier = isNaN(parsed) ? 1 : parsed;
                        }
                    }
                }));
            });
        });

        if (!force && !dirty && _.isEqual(opportunity.offers, newOffers) && opportunity.status !== 'draft') return true;

        setSaving(true);

        try {
            if (account.status === 'draft') {
                const { data: newAccount } = await axios.put(`/api/accounts/${account.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                    account: {
                        name: account.name,
                        tags: [{ label: 'Pipeline: New' }],
                    }
                });
                setAccounts(acc => [...acc.filter(a => a.id !== account.id), newAccount ]);
                setAccount(_.pick(newAccount, ['id', 'name', 'tags']));
            }

            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                opportunity: {
                    ..._.omit(opportunity, ['id', 'created', 'createdBy', 'owner', 'organizationId', 'log', 'modified']),
                    status: status === 'draft' ? null : status || null,
                    type,
                    account: _.pick(account, ['id']),
                    tags,
                    incoterm,
                    products: minimizeProducts({ products }),
                    offers: newOffers,
                    transportationMethod,
                    routes,
                    preface,
                    epilogue,
                    source,
                    terms: opportunity.terms, // Explicitly include terms to ensure empty arrays are saved
                    buyingRates: (opportunity.buyingRates || []).map(br => ({ ...br, product: minimizeProducts({ products: [br.product] })[0] })),
                    exchangeRates: lockedExchangeRates,
                }
            });

            onUpdateOpportunity({ opportunity: _opportunity });
            setStatus(_opportunity.status);
            setSaving(false);
            setDirty(false);
            return true;
        } catch (e) {
            setSaving(false);
            console.error(e);
            if (e.response) {
                setSaveError(e.response.data.detail || e.response.data.title || e.response.data);
            } else {
                setSaveError(e);
            }
            return false;
        }
    }

    // Auto-save for drafts: debounce 3 seconds after any change. No separate
    // notification — it drives the header Save button's own saving/saved state.
    const autoSaveTimer = useRef(null);
    const isDraft = !status || status === 'draft';

    // Only auto-save once the mandatory shipment-details fields are filled in
    // (Account, Transportation Method, Incoterm, and the required product fields).
    const mandatoryFilled = (() => {
        try {
            const er = getErrors({ opportunity: {
                account, transportationMethod, incoterm,
                products: products || [],
                offers: opportunity.offers || [],
            } });
            return er.account.length === 0 && er.incoterm.length === 0 && er.products.length === 0;
        } catch { return false; }
    })();

    const autoSave = useCallback(async () => {
        if (!isDraft || !dirty || !opportunityId || !mandatoryFilled) return;
        setSaving(true);
        try {
            const { data: _savedOpportunity } = await axios.put(`/api/opportunities/${opportunityId}?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                opportunity: {
                    ..._.omit(opportunity, ['id', 'created', 'createdBy', 'owner', 'organizationId', 'log', 'modified']),
                    status: null,
                    type,
                    account: _.pick(account, ['id']),
                    tags,
                    incoterm,
                    products: minimizeProducts({ products }),
                    offers: opportunity.offers,
                    transportationMethod,
                    routes,
                    preface,
                    epilogue,
                    source,
                    terms: opportunity.terms,
                    buyingRates: (opportunity.buyingRates || []).map(br => ({ ...br, product: minimizeProducts({ products: [br.product] })[0] })),
                    exchangeRates: lockedExchangeRates,
                }
            });
            setDirty(false);
            // Reflect the saved state locally: autosave persists status:null, so clear the local
            // 'draft' too (matching the manual Save path at save()). `isDraft` stays true for null,
            // so autosave keeps working — but opportunity.status is no longer 'draft', so features
            // gated on "is this saved?" (e.g. the RFQs to Suppliers pane) light up right after
            // autosave instead of forcing the user to click Save.
            setStatus(null);
            // Sync the local `modified` to the server's so the background poll
            // (which overwrites only when serverModified > localModified) doesn't
            // clobber sections/fields added after this save with an older snapshot.
            // Only status + the timestamp are touched — local edits/content are preserved.
            onUpdateOpportunity({ opportunityId, func: o => ({ ...o, status: null, modified: _savedOpportunity?.modified ?? o.modified }) });
            setSaving(false);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000);
        } catch (e) {
            console.error('[auto-save]', e);
            setSaving(false); // fail silently — the unsaved state and a manual Save surface any error
        }
    }, [isDraft, dirty, mandatoryFilled, opportunityId, accesstoken, org.id, opportunity, type, account, tags, incoterm, products, transportationMethod, routes, preface, epilogue, source, lockedExchangeRates, onUpdateOpportunity]);

    useEffect(() => {
        if (!isDraft || !dirty || !mandatoryFilled) return;
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(autoSave, 3000);
        return () => clearTimeout(autoSaveTimer.current);
    }, [isDraft, dirty, mandatoryFilled, autoSave]);

    // Register/clear this opportunity in the dirty registry so the background
    // poll in Main never overwrites it while it has unsaved local edits (which
    // would make just-added sections/fields disappear). `dirty` stays true until
    // a save completes, so this also covers the in-flight save window.
    useEffect(() => {
        if (!opportunityId) return;
        if (dirty) markOpportunityDirty(opportunityId);
        else markOpportunityClean(opportunityId);
    }, [dirty, opportunityId]);
    useEffect(() => () => markOpportunityClean(opportunityId), [opportunityId]);

    return (
        <Box>
            <OpportunityHeader 
                org={org}
                activeStep={activeStep}
                onNextLoading={onNextLoading}
                onNextDisabled={onNextDisabled}
                status={status}
                saveSuccess={saveSuccess}
                saving={saving}
                saveError={saveError}
                setSaveError={setSaveError}
                saveErrorDescription={errors.header || []}
                saveDisabled={saveDisabled}
                onSave={() => onSave({ force: true })}
                onNextStep={onNextStep}
                onPrevStep={onPrevStep}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onDuplicateDisabled={dirty}
                onReopen={() => setShowReopen(true)}
                onTakeOwnership={() => setOwnership(true)}
                account={account}
                opportunity={opportunity}
                onUpdateOpportunity={onUpdateOpportunity}
                accesstoken={accesstoken}
                owner={(opportunity || {}).owner || me.email}
                me={me}
                minimizedSearch={minimizedSearch}
                tags={tags}
                setTags={_setTags}
                possibleTags={possibleTags || []}
                tagsError={tagsError}
                setTagsError={setTagsError}
                onEmailRefreshConnection={onEmailRefreshConnection}
                onEmailLogout={onEmailLogout}
                emailLoginInfo={emailLoginInfo}
                lockedExchangeRates={lockedExchangeRates?.locked}
                setLockedExchangeRates={onSetLockedExchangeRates}
            />

            {activeStep === 1 && (
                <>
                    <OpportunityOneOffAccountRouteProductsSelector
                        org={org}
                        me={me}
                        account={account}
                        setAccount={setAccount}
                        routes={routes}
                        setRoutes={_setRoutes}
                        incoterm={incoterm}
                        setIncoterm={_setIncoterm}
                        products={products}
                        setProducts={setProducts}
                        seaPorts={seaPorts}
                        description={description}
                        setDescription={_setDescription}
                        transportationMethod={transportationMethod}
                        setTransportationMethod={_setTransportationMethod}
                        unlocode={unlocode}
                        accesstoken={accesstoken}
                        accounts={accounts}
                        source={source}
                        status={status}
                        updates={opportunity?.updates || []}
                        setUpdates={setUpdates}
                        emailLoginInfo={emailLoginInfo}
                        locked={status && status !== 'draft'}
                        opportunity={opportunity}
                    />
                    <Builder
                        errors={errors.builder || []}
                        routes={routes}
                        setRoutes={_setRoutes}
                        offers={opportunity?.offers || []}
                        setOffers={_setOffers}
                        buyingRates={opportunity?.buyingRates || []}
                        setBuyingRates={setBuyingRates}
                        products={products}
                        setProducts={setProducts}
                        org={org}
                        minimizedSearch={minimizedSearch}
                        seaPorts={seaPorts}
                        unlocode={unlocode}
                        suppliers={suppliers}
                        transportationMethod={transportationMethod}
                        setTransportationMethod={_setTransportationMethod}
                        opportunityId={opportunity?.id}
                        status={(opportunity || {}).status}
                        onCreateTemplate={onCreateTemplate}
                        owner={(opportunity || {}).owner || me.email}
                        me={me}
                        accesstoken={accesstoken}
                        exchangeRates={exchangeRates}
                        contracts={contracts}
                        account={account}
                        accounts={accounts}
                        incoterm={incoterm}
                        setIncoterm={_setIncoterm}
                        rateRequests={opportunity?.rateRequests || []}
                        preface={preface}
                        setPreface={_setPreface}
                        epilogue={epilogue}
                        source={source}
                        setEpilogue={_setEpilogue}
                        dirty={dirty}
                        // onSave holds the "Saved" tick on screen for two seconds before it
                        // resolves, which is fine for a button and not for something waiting
                        // on the save to finish. This is the same save without that pause.
                        onSaveNow={() => save({ force: true })}
                        terms={opportunity.terms}
                        setTerms={terms => {
                            setDirty(true);
                            onUpdateOpportunity({opportunityId, func: opportunity => ({...opportunity, terms}) })
                        }}
                        featureToggle={featureToggle}
                        lockedExchangeRates={lockedExchangeRates}
                        opportunities={opportunities}
                        saveSuccess={saveSuccess}
                        saving={saving}
                        saveError={saveError}
                        saveErrorDescription={errors.header || []}
                        onSave={() => onSave({ force: true })}
                        attachPDF={attachPDF}
                        setAttachPDF={_setAttachPDF}
                        opportunity={opportunity}
                        onUpdateOpportunity={onUpdateOpportunity}
                    />
                    <Box sx={{ position: 'sticky', bottom: 0, zIndex: 10 }}>
                        <FinancialSummaryBar opportunity={opportunity} exchangeRates={exchangeRates} organization={org} lockedExchangeRates={lockedExchangeRates} />
                    </Box>
                </>
            )}
            {activeStep === 2 && (
                <>
                    <PreviewAndSend
                        org={org}
                        me={me}
                        accounts={accounts}
                        accesstoken={accesstoken}
                        opportunity={opportunity}
                        organization={org}
                        onUpdateOpportunity={e => {
                            if (e.opportunity) {
                                setStatus(e.opportunity.status || null);
                            }
                            onUpdateOpportunity(e)
                        }}
                        owner={(opportunity || {}).owner || me.email}
                        suppliers={suppliers}
                        seaPorts={seaPorts}
                        unlocode={unlocode}
                        tags={tags}
                        setTags={_setTags}
                        exchangeRates={exchangeRates}
                        preface={preface}
                        epilogue={epilogue}
                        emailLoginInfo={emailLoginInfo}
                        onEmailRefreshConnection={onEmailRefreshConnection}
                        onEmailLogout={onEmailLogout}
                        lockedExchangeRates={lockedExchangeRates}
                        onLockExchangeRates={(v) => {
                            onSetLockedExchangeRates(v);
                            setTimeout(() => {
                                onSaveRef.current();
                            }, 100);
                        }}
                        attachPDF={attachPDF}
                        setAttachPDF={_setAttachPDF}
                    />
                </>
            )}
            {showReopen && (
                <ReopenDialog 
                    org={org}
                    opportunity={opportunity}
                    accesstoken={accesstoken}
                    onUpdateOpportunity={({ opportunity }) => { 
                        setStatus(opportunity.status || null); 
                        onUpdateOpportunity({ opportunity });
                    }}
                    onClose={() => setShowReopen(false)} 
                />
            )}
            {ownership && (
                <OwnershipDialog
                    me={me}
                    org={org}
                    opportunity={opportunity}
                    accesstoken={accesstoken}
                    onUpdateOpportunity={onUpdateOpportunity}
                    account={account}
                    onClose={() => setOwnership(false)}
                />
            )}
            {backgroundEmailMessage && (
                <Snackbar
                    open={!!backgroundEmailMessage}
                    autoHideDuration={6000}
                    onClose={() => setBackgroundEmailMessage(null)}
                >
                    <Alert severity={backgroundEmailMessage.severity} onClose={() => setBackgroundEmailMessage(null)}>
                        {backgroundEmailMessage.message}
                    </Alert>
                </Snackbar>
            )}
        </Box>
    );
}

export default OpportunityOneOffScreen;

function ReopenDialog(props) {
    const { onClose, org, accesstoken, onUpdateOpportunity, opportunity } = props;
    const [ creating, setCreating ] = useState(false);
    const [ error, setError ] = useState(false);
    const [ success, setSuccess ] = useState(false);

    async function onOK() {
        setCreating(true);
        setError(false);
        setSuccess(false);

        try {
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                opportunity: { status: null }
            });
            onUpdateOpportunity({ opportunity: _opportunity });
            setSuccess(true);
            return;
        } catch(e) {
            setCreating(false);
            setError(true);
            return;
        }
    }

    if (success) {
        return (
            <Dialog open={true} onClose={onClose} PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
                <Box sx={{ p: 4, textAlign: 'center', width: '420px' }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '50%', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                        <FontAwesomeIcon icon={faCircleCheck} style={{ color: '#10b981', fontSize: '28px' }} />
                    </Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', mb: 0.5 }}>Quote Reopened</Typography>
                    <Typography sx={{ fontSize: '14px', color: '#64748b', mb: 3 }}>You can now edit and resend the quote. Previously shared links will redirect to the updated version.</Typography>
                    <Button variant='contained' onClick={onClose} sx={{ textTransform: 'none', borderRadius: '10px', px: 4, background: '#10b981', '&:hover': { background: '#059669' } }}>
                        Continue Editing
                    </Button>
                </Box>
            </Dialog>
        )
    }

    return (
        <Dialog open={true} onClose={onClose} PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
            <Box sx={{ p: 4, width: '420px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#f0f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FontAwesomeIcon icon={faRotateRight} style={{ color: '#6366f1', fontSize: '16px' }} />
                    </Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Reopen Quote</Typography>
                </Box>
                {creating ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : (
                    <>
                        <Typography sx={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, mb: 1 }}>
                            This will unlock the quote for editing. Any links you've shared will automatically show the updated version once you regenerate the link.
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5 }}>
                            No need to resend — your customer will see the latest changes through the same link.
                        </Typography>
                    </>
                )}
                {error && (
                    <Alert severity='error' sx={{ mt: 2, borderRadius: '10px' }}>
                        Error reopening quote. Please try again later.
                    </Alert>
                )}
                {!creating && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
                        <Button variant='outlined' onClick={onClose} sx={{ textTransform: 'none', borderRadius: '10px', color: '#64748b', borderColor: '#e2e8f0', '&:hover': { borderColor: '#cbd5e1' } }}>
                            Cancel
                        </Button>
                        <Button variant='contained' onClick={onOK} sx={{ textTransform: 'none', borderRadius: '10px', px: 3, background: '#6366f1', '&:hover': { background: '#4f46e5' } }}>
                            Reopen Quote
                        </Button>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
}

function OwnershipDialog(props) {
    const { onClose, account, me, onUpdateOpportunity, accesstoken, opportunity, org } = props;
    const [ circular, setCircular ] = useState(false);
    const [ error, setError ] = useState(null);

    async function handleTransfer() {
        setError(null);
        setCircular(true);
        try {
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                owner: me.email
            });

            setCircular(false);
            onUpdateOpportunity({ opportunity: _opportunity });
            onClose();

        } catch (e) {
            setCircular(false);
            console.log(e);
            setError(`Error transfering ownership: ${e.response.data.detail}`);
        }
    }

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle>
                Ready to Take Ownership?
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    You're about to become the proud owner of <b>{account.name}</b>’s opportunity!
                    <br/><br/>
                    But before you take charge, please note that as the new owner, you'll be responsible for pricing, quoting, and following up with the customer regarding this inquiry.
                    <br/><br/>
                    {!circular && (
                        <Box>
                            Ready to take ownership?
                        </Box>
                    )}
                    
                    {circular && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {error && (
                        <Typography component={Box} variant='caption' sx={{ color: red[500], paddingTop: '24px' }} >
                            {error}
                        </Typography>
                    )}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleTransfer} autoFocus disabled={circular}>
                    Sure Do!
                </Button>
                <Button color='secondary' onClick={onClose} disabled={circular}>
                    No
                </Button>
            </DialogActions>
        </Dialog>
    )
}
